import { LitElement, html, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import type { ConversationMessage } from '../../types/conversation.js';
import './cp-chat-message.js';

@customElement('cp-chat-panel')
export class CpChatPanel extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Array }) messages: ConversationMessage[] = [];
  @property({ type: Boolean }) loading = false;

  @state() private _inputValue = '';
  @query('.cp-chat-panel__messages') private _messagesContainer!: HTMLElement;
  @query('.cp-chat-panel__input') private _inputEl!: HTMLTextAreaElement;

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('messages') || changedProperties.has('loading')) {
      this._scrollToBottom();
    }
  }

  private _scrollToBottom(): void {
    requestAnimationFrame(() => {
      if (this._messagesContainer) {
        this._messagesContainer.scrollTop = this._messagesContainer.scrollHeight;
      }
    });
  }

  private _onInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this._inputValue = textarea.value;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this._sendMessage();
    }
  }

  private _sendMessage(): void {
    const content = this._inputValue.trim();
    if (!content || this.loading) return;

    this.dispatchEvent(new CustomEvent('message-send', {
      bubbles: true,
      composed: true,
      detail: { content },
    }));

    this._inputValue = '';
    if (this._inputEl) {
      this._inputEl.value = '';
      this._inputEl.style.height = 'auto';
      this._inputEl.focus();
    }
  }

  private _renderEmptyState() {
    return html`
      <div class="cp-chat-panel__empty">
        <span class="cp-chat-panel__empty-text">Starte die Konversation...</span>
      </div>
    `;
  }

  private _renderLoading() {
    return html`
      <div class="cp-chat-panel__loading">
        <span class="cp-chat-panel__loading-dot"></span>
        <span class="cp-chat-panel__loading-dot"></span>
        <span class="cp-chat-panel__loading-dot"></span>
      </div>
    `;
  }

  render() {
    const hasMessages = this.messages.length > 0;

    return html`
      <div class="cp-chat-panel">
        <div class="cp-chat-panel__messages">
          ${hasMessages
            ? this.messages.map(msg => html`
                <cp-chat-message .message=${msg}></cp-chat-message>
              `)
            : this._renderEmptyState()
          }
          ${this.loading ? this._renderLoading() : nothing}
        </div>

        <div class="cp-chat-panel__input-area">
          <textarea
            class="cp-chat-panel__input"
            placeholder="Nachricht eingeben..."
            rows="3"
            .value=${this._inputValue}
            @input=${this._onInput}
            @keydown=${this._onKeyDown}
            ?disabled=${this.loading}
          ></textarea>
          <button
            class="cp-chat-panel__send"
            @click=${this._sendMessage}
            ?disabled=${!this._inputValue.trim() || this.loading}
            title="Senden"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-chat-panel': CpChatPanel;
  }
}
